import { Component, Element, Host, h, Prop, Watch } from "@stencil/core";

/**
 * This component represents a detail view in the `<ux-iws-nb-all-in-one>` module.
 * It is used to display detailed information about a product or service, including
 * an image, title, text content, features, specifications, and a call-to-action button.
 * The component is used in conjunction with `<ux-iws-nb-all-in-one-detail-group>`
 * and `<ux-iws-nb-all-in-one-tile>`.
 *
 * @slot image - Slot for the image (`<picture>` or `<img>`)
 * @slot title - Slot for the headline (`<h2>` or `<h3>`)
 * @slot cta - Slot for the call-to-action button (`<ux-button>`)
 * @slot features - Slot for the list with product key features (`<ul>`)
 * @slot specs - Slot for additional specifications (richtext)
 * @slot (default) - Slot for the text content (rich text)
 */
@Component({
    tag: "ux-iws-nb-all-in-one-detail",
    styleUrl: "nb-all-in-one-detail.scss",
    shadow: false,
})
export class NbAllInOneDetail {
    private static uidIncrement = 0;
    private uid = `nb-all-in-one-detail-${NbAllInOneDetail.uidIncrement++}`;

    /** The element reference for the component. */
    @Element() host: HTMLUxIwsNbAllInOneDetailElement;

    /**
     * Label of the detail section (accessibility).
     * Also used for the corresponding tab button or dropdown option in the
     * `<ux-iws-nb-all-in-one-group>` component.
     */
    @Prop({ reflect: true }) label: string = "";

    /** @deprecated Category label shown above the headline */
    @Prop({ reflect: true }) category: string = "";

    /** Indicates if the section is active or not */
    @Prop({ mutable: true, reflect: true }) active: boolean = false;

    /** Variant of the detail section */
    @Prop({ reflect: true }) variant: "default" | "detail" = "default";

    /** Comma separated list of tags */
    @Prop() tags: string = "";

    /** Label for the list of tags (accessibility) */
    @Prop() tagsLabel: string = "";

    /** Label for the list of features (accessibility) */
    @Prop() featuresLabel: string = "";

    /**
     * Watches for changes to the 'active' property and ensures only this detail is active among its siblings.
     * @param active - Indicates if this detail should be active.
     */
    @Watch("active")
    handleActiveChange(active: boolean) {
        if (!active) {
            return;
        }
        const siblings =
            this.host.parentElement.querySelectorAll<HTMLUxIwsNbAllInOneDetailElement>(
                "ux-iws-nb-all-in-one-detail"
            );
        siblings.forEach((el) => (el.active = el === this.host));
    }

    render() {
        const baseClass = "ux-iws-nb-all-in-one-detail";
        const hasCtaSlot = !!this.host.querySelector("[slot='cta']");
        const hasImageSlot = !!this.host.querySelector("[slot='image']");
        const hasTitleSlot = !!this.host.querySelector("[slot='title']");
        const hasFeaturesSlot = !!this.host.querySelector("[slot='features']");
        const hasSpecsSlot = !!this.host.querySelector("[slot='specs']");
        const tags = this.tags
            .split(",")
            .map((tag) => tag.trim())
            .filter((tag) => tag !== "");
        let gridMode: string = "default";
        if (!hasImageSlot && !hasFeaturesSlot) {
            gridMode = "text";
        } else if (hasImageSlot && (hasFeaturesSlot || hasSpecsSlot)) {
            gridMode = "image-text-features";
        } else if (hasImageSlot) {
            gridMode = "image-text";
        }
        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--active`]: this.active,
                }}
                role="tabpanel"
                aria-label={this.label}
                aria-hidden={this.active ? "false" : "true"}
                id={this.uid}
            >
                <article
                    class={{
                        [`${baseClass}__grid`]: true,
                        [`${baseClass}__grid--${gridMode}`]:
                            gridMode !== "default",
                    }}
                    aria-label={hasTitleSlot ? undefined : this.label}
                    aria-labelledby={
                        hasTitleSlot ? `${this.uid}-title` : undefined
                    }
                    aria-describedby={`${this.uid}-content`}
                >
                    <div
                        class={
                            hasImageSlot ? `${baseClass}__image` : "is-hidden"
                        }
                        aria-hidden={hasImageSlot ? "false" : "true"}
                    >
                        <slot name="image" />
                    </div>
                    <section
                        class={`${baseClass}__content`}
                        id={`${this.uid}-content`}
                    >
                        <div
                            class={
                                hasTitleSlot
                                    ? `${baseClass}__title`
                                    : "is-hidden"
                            }
                            aria-hidden={hasImageSlot ? "false" : "true"}
                            id={`${this.uid}-title`}
                        >
                            <slot name="title" />
                        </div>
                        <div class={`${baseClass}__text`}>
                            <slot />
                        </div>
                        {tags.length > 0 && (
                            <section
                                class={`${baseClass}__tag-list`}
                                aria-label={this.tagsLabel || undefined}
                            >
                                {tags.map((tag) => (
                                    <span class={`${baseClass}__tag`} key={tag}>
                                        {tag}
                                    </span>
                                ))}
                            </section>
                        )}
                    </section>
                    <section
                        class={
                            hasFeaturesSlot
                                ? `${baseClass}__features`
                                : "is-hidden"
                        }
                        aria-hidden={hasFeaturesSlot ? "false" : "true"}
                        aria-label={
                            hasFeaturesSlot ? this.featuresLabel : undefined
                        }
                    >
                        <slot name="features" />
                    </section>
                    <div
                        class={
                            hasSpecsSlot ? `${baseClass}__specs` : "is-hidden"
                        }
                    >
                        <slot name="specs" />
                    </div>
                    <div class={hasCtaSlot ? `${baseClass}__cta` : "is-hidden"}>
                        <slot name="cta" />
                    </div>
                </article>
            </Host>
        );
    }
}
